/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IProductSpecificationProps as IProductSpecificationExtentionProps } from '../definition-extensions/product-specification.ext.props.autogenerated';

import { IProductSpecificationData, IProductSpecificationViewProps } from '@msdyn365-commerce-modules/product-specification';
import * as Msdyn365 from '@msdyn365-commerce/core';

const ProductSpecificationView: React.FC<IProductSpecificationViewProps &
    IProductSpecificationExtentionProps<IProductSpecificationData>> = props => {
    const {
        ProductSpecification,
        ProductSpecificationTableProps,
        ProductSpecificationTableBodyProps,
        productSpecificationResult,
        config
    } = props;

    // Extract excluded attributes from the config
    const excludedAttributes = config?.excludedAttributes ? config.excludedAttributes.split(',').map(attr => attr.trim()) : [];

    // Filter rowdata based key property
    const downloadableContent = Array.isArray(productSpecificationResult)
        ? productSpecificationResult.filter(
              row => row?.key === 'additional_media_drawer' // Filter items for downloadable content
          )
        : [];

    const extractDownloads = (node: any): { href: string; text: string }[] => {
        if (!node) return [];

        const downloads: { href: string; text: string }[] = [];

        // Check if the node itself is a link
        if (node?.props?.href) {
            downloads.push({
                href: node.props.href,
                text: node.props.children || 'Download'
            });
        }

        // If the node has children, recursively extract downloads from them
        if (Array.isArray(node.props?.children)) {
            node.props.children.forEach((child: any) => {
                downloads.push(...extractDownloads(child));
            });
        } else if (node.props?.children) {
            downloads.push(...extractDownloads(node.props.children));
        }

        return downloads;
    };

    // extract the actual downloads
    const actualDownloads = downloadableContent.flatMap(item => extractDownloads(item));

    // Filter rowdata based on excludedAttributes and key property
    let rowdata =
        productSpecificationResult &&
        productSpecificationResult
            .filter(
                row => !ObjectExtensions.isNullOrUndefined(row) && !excludedAttributes.includes(row.props?.children?.props?.productName) // Exclude null/undefined // Exclude based on productName
            )
            .filter(
                row => row?.key !== 'additional_media_drawer' // Exclude rows with key "additional_media_drawer"
            )
            .filter(row => row?.props?.children?.props?.cellData.props.dangerouslySetInnerHTML !== undefined);

    // Handle display logic based on hsoDisplayDownloadableContent
    if (config?.className) {
        const displayOption = config.className;

        if (displayOption === 'hsodisplaydownloadablecontent__onlyDownloads') {
            // Replace `rowdata` with React elements for downloads
            rowdata = actualDownloads.map((download, index) => (
                <li key={index}>
                    <span>{createDownloadName(download.text)}</span>
                    <a href={download.href} target='_blank' rel='noopener noreferrer'>
                        {download.text || `Download ${index + 1}`}
                    </a>
                </li>
            ));
        } else if (displayOption === 'hsodisplaydownloadablecontent__both') {
            // Combine specifications and downloadable content as React elements
            const downloadElements = actualDownloads.map((download, index) => (
                <li key={`download-${index}`}>
                    <span>{createDownloadName(download.text)}</span>
                    <a href={download.href} target='_blank' rel='noopener noreferrer'>
                        {download.text || `Download ${index + 1}`}
                    </a>
                </li>
            ));

            // Combine both specifications and downloads
            rowdata = [...(rowdata || []), ...downloadElements];
        } else if (displayOption === 'hsodisplaydownloadablecontent__displayDescription') {
            const productDescription = props.data.product.result?.ExtensionProperties?.filter(
                property => property.Key === props.app.config.hsoRexelProductDescriptionAttribute
            );
            if (props.app.config.hsoRexelProductDescription && productDescription && productDescription.length > 0) {
                return (
                    <Module {...ProductSpecification}>
                        <Msdyn365.RichTextComponent
                            text={productDescription[0]?.Value?.StringValue}
                            className='ms-buybox__product-description'
                        />
                    </Module>
                );
            } else {
                return null;
            }
        }
    }

    return (
        <Module {...ProductSpecification}>
            {/* Product Specifications Tab */}
            <Node {...ProductSpecificationTableProps}>
                <Node {...ProductSpecificationTableBodyProps}>{rowdata}</Node>
            </Node>
        </Module>
    );
};

const createDownloadName = (downloadName: string): string => {
    let name = '';
    // Check if the download is a data sheet
    const matchDataSheet = /DATA/;
    if (matchDataSheet.test(downloadName.toUpperCase())) {
        name = 'Data Sheet: ';
    }

    const matchUserManual = /USER/;
    if (matchUserManual.test(downloadName.toUpperCase())) {
        name = 'User Manual: ';
    }

    const matchCertificate = /CERTIFICATE/;
    if (matchCertificate.test(downloadName.toUpperCase())) {
        name = 'Certificate: ';
    }

    const matchQuickUserGuide = /QUICK/;
    if (matchQuickUserGuide.test(downloadName.toUpperCase())) {
        name = 'Quick User Guide: ';
    }

    // Implement further checks for other types of downloads here
    return name;
};

export default ProductSpecificationView;
